import { createSystem, defaultConfig } from '@chakra-ui/react'

const system = createSystem(defaultConfig, {
    theme: {
        tokens: {
            fonts: {
                heading: { value: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif' },
                body: { value: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif' },
                mono: { value: '"JetBrains Mono", SFMono-Regular, Menlo, Monaco, Consolas, monospace' },
            },
            colors: {
                // Professional Light Palette (Zinc-based)
                gray: {
                    50: { value: '#fafafa' },
                    100: { value: '#f4f4f5' },
                    200: { value: '#e4e4e7' },
                    300: { value: '#d4d4d8' },
                    400: { value: '#a1a1aa' },
                    500: { value: '#71717a' },
                    600: { value: '#52525b' },
                    700: { value: '#3f3f46' },
                    800: { value: '#27272a' },
                    900: { value: '#18181b' },
                    950: { value: '#09090b' },
                },
                // Trustworthy Blue
                brand: {
                    50: { value: '#eff6ff' },
                    100: { value: '#dbeafe' },
                    200: { value: '#bfdbfe' },
                    300: { value: '#93c5fd' },
                    400: { value: '#60a5fa' },
                    500: { value: '#3b82f6' }, // Primary
                    600: { value: '#2563eb' },
                    700: { value: '#1d4ed8' },
                    800: { value: '#1e40af' },
                    900: { value: '#1e3a8a' },
                    950: { value: '#172554' },
                },
                // Trading Colors
                trade: {
                    up: { value: '#10b981' }, // Emerald 500
                    down: { value: '#ef4444' }, // Red 500
                },
                accent: {
                    cyan: { value: '#06b6d4' },
                },
            },
        },
        semanticTokens: {
            colors: {
                bg: {
                    canvas: { value: '{colors.gray.50}' },
                    surface: { value: '#ffffff' },
                    subtle: { value: '{colors.gray.100}' },
                },
                border: {
                    subtle: { value: '{colors.gray.200}' },
                    default: { value: '{colors.gray.300}' },
                    active: { value: '{colors.brand.500}' },
                },
                text: {
                    main: { value: '{colors.gray.900}' },
                    muted: { value: '{colors.gray.500}' },
                    subtle: { value: '{colors.gray.600}' },
                    brand: { value: '{colors.brand.600}' },
                    inverted: { value: '#ffffff' },
                },
            },
        },
        recipes: {
            button: {
                baseStyle: {
                    borderRadius: 'md',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    _active: { transform: 'scale(0.98)' },
                },
                variants: {
                    solid: {
                        bg: 'brand.600',
                        color: 'white',
                        _hover: { bg: 'brand.700' },
                    },
                    outline: {
                        borderColor: 'border.default',
                        color: 'text.main',
                        bg: 'white',
                        _hover: { bg: 'bg.subtle', borderColor: 'text.muted' },
                    },
                    ghost: {
                        color: 'text.subtle',
                        _hover: { bg: 'bg.subtle', color: 'text.main' },
                    },
                    glass: {
                        bg: 'rgba(255, 255, 255, 0.7)',
                        backdropFilter: 'blur(8px)',
                        color: 'text.main',
                        border: '1px solid',
                        borderColor: 'border.subtle',
                        _hover: { bg: 'rgba(255, 255, 255, 0.9)' },
                    }
                }
            },
            card: {
                baseStyle: {
                    bg: 'bg.surface',
                    borderRadius: 'lg',
                    border: '1px solid',
                    borderColor: 'border.subtle',
                    shadow: 'sm',
                },
                variants: {
                    glass: {
                        bg: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid',
                        borderColor: 'border.subtle',
                        shadow: 'md',
                    }
                }
            },
            input: {
                baseStyle: {
                    field: {
                        bg: 'white',
                        borderRadius: 'md',
                        borderColor: 'border.default',
                        color: 'text.main',
                        _placeholder: { color: 'text.muted' },
                        _focus: {
                            borderColor: 'brand.500',
                            ring: '2px',
                            ringColor: 'brand.100',
                            bg: 'white',
                        }
                    }
                }
            }
        }
    },
    globalCss: {
        'html, body': {
            bg: 'bg.canvas',
            color: 'text.main',
            fontFamily: 'body',
        },
        // Refined Scrollbar (Light)
        '::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
        },
        '::-webkit-scrollbar-track': {
            bg: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
            bg: 'gray.300',
            borderRadius: 'full',
            '&:hover': {
                bg: 'gray.400',
            },
        },
    }
})

export default system
